'use client';

import { useRef, useState } from 'react';
import { cn } from '@/src/shared/lib/utils';

interface FileUploadButtonProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  maxSizeMB?: number;
  variant?: 'default' | 'icon';
}

export const FileUploadButton = ({
  onFilesSelected,
  disabled = false,
  maxFiles = 10,
  maxSizeMB = 5,
  variant = 'default',
}: FileUploadButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const accept =
    'image/png,image/jpeg,image/jpg,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  const validateFiles = (
    files: File[]
  ): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const allowedTypes = accept.split(',');

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type`);
        continue;
      }

      if (file.size > maxSizeBytes) {
        errors.push(`${file.name}: File too large (max ${maxSizeMB}MB)`);
        continue;
      }

      valid.push(file);
    }

    if (valid.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`);
      return { valid: valid.slice(0, maxFiles), errors };
    }

    return { valid, errors };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const { valid, errors } = validateFiles(files);

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (valid.length > 0) {
      onFilesSelected(valid);
    }

    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const { valid, errors } = validateFiles(files);

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (valid.length > 0) {
      onFilesSelected(valid);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={disabled}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
        className={cn(
          'transition-colors',
          variant === 'default' && 'p-2 rounded-lg',
          variant === 'icon' && 'p-0',
          isDragging && 'bg-accent-primary/20',
          disabled
            ? 'text-gray-400 cursor-not-allowed'
            : variant === 'icon'
              ? 'text-text-primary hover:opacity-70'
              : 'text-gray-600 hover:bg-gray-100'
        )}
        title="Attach file"
        aria-label="Attach file"
      >
        <svg
          width={variant === 'icon' ? '32' : '20'}
          height={variant === 'icon' ? '32' : '20'}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        aria-hidden="true"
      />
    </>
  );
};
